/**
 * @fileoverview API utilities for variant resolution status data flow.
 * @module features/sku-alias/api/resolutionStatus
 */

import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import apiClient from '@/lib/apiClient'
import {
  masterVariantIdSchema,
  skuAliasIdSchema,
  skuAliasSourceSchema,
  type MasterVariantId,
} from '@/features/sku-alias/schemas/skuAliasSchema'

export const resolutionMetricsSchema = z.object({
  salesCount: z.number().int().nonnegative().default(0),
  revenue: z.number().default(0),
  adSpendCount: z.number().int().nonnegative().default(0),
  adSpend: z.number().default(0),
  lastSeenSale: z.string().nullable().optional(),
  lastSeenAdSpend: z.string().nullable().optional(),
  externalSlugs: z.array(z.string()).default([]),
})

const resolutionAliasSchema = z.object({
  id: skuAliasIdSchema,
  slug: z.string(),
  source: skuAliasSourceSchema,
})

export type SkuResolutionAlias = z.infer<typeof resolutionAliasSchema>

const resolutionTypeSchema = z.enum(['CANONICAL_MATCH', 'EXPLICIT_ALIAS', 'MIXED', 'NO_ACTIVITY'])

export const skuResolutionStatusSchema = z.object({
  masterVariantId: masterVariantIdSchema,
  masterSkuName: z.string(),
  resolutionType: resolutionTypeSchema,
  externalSlugs: z.array(z.string()).default([]),
  aliases: z.array(resolutionAliasSchema).default([]),
  totals: resolutionMetricsSchema,
})

export type SkuResolutionStatus = z.infer<typeof skuResolutionStatusSchema>

function unwrapResolutionStatusPayload(payload: unknown): unknown {
  let current = payload
  for (let i = 0; i < 2; i += 1) {
    if (current && typeof current === 'object' && 'data' in (current as Record<string, unknown>)) {
      const nested = (current as { data?: unknown }).data
      if (nested !== undefined) {
        current = nested
        continue
      }
    }
    break
  }
  return current
}

/**
 * Generate query key for variant resolution status requests.
 * @param variantId Master Variant identifier.
 * @returns Stable query key tuple.
 */
export const variantResolutionStatusQueryKey = (
  variantId: MasterVariantId | number | null | undefined
) =>
  ['variant-resolution-status', variantId ?? null] as const

/** @deprecated Use variantResolutionStatusQueryKey instead */
export const skuResolutionStatusQueryKey = variantResolutionStatusQueryKey

/**
 * Fetch resolution status for a variant.
 * @param variantId Master Variant identifier.
 * @returns Parsed resolution status data.
 */
export async function getVariantResolutionStatus(
  variantId: MasterVariantId | number
): Promise<SkuResolutionStatus> {
  const parsedId = masterVariantIdSchema.parse(variantId)
  const response = await apiClient.get(`/variants/${parsedId}/resolution-status`)
  const payload = unwrapResolutionStatusPayload(response.data)
  return skuResolutionStatusSchema.parse(payload)
}

/** @deprecated Use getVariantResolutionStatus instead */
export const getSkuResolutionStatus = getVariantResolutionStatus

/**
 * React Query hook for variant resolution status.
 * @param variantId Master Variant identifier or null to skip.
 * @param options Optional React Query overrides.
 */
export function useVariantResolutionStatusQuery(
  variantId: MasterVariantId | number | null | undefined,
  options?: Omit<UseQueryOptions<SkuResolutionStatus, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<SkuResolutionStatus, Error> {
  const normalizedId =
    typeof variantId === 'number' && Number.isFinite(variantId) ? variantId : variantId ?? null
  return useQuery<SkuResolutionStatus, Error>({
    queryKey: variantResolutionStatusQueryKey(normalizedId),
    queryFn: async () => {
      if (normalizedId === null) {
        throw new Error('Master variant id is required')
      }
      return getVariantResolutionStatus(normalizedId)
    },
    enabled: normalizedId !== null && (options?.enabled ?? true),
    ...options,
  })
}

/** @deprecated Use useVariantResolutionStatusQuery instead */
export const useSkuResolutionStatusQuery = useVariantResolutionStatusQuery
